import React from "react";
import { TextInputField } from "../../bootstrap/TextInputField";
import { NumberInputField } from "../../bootstrap/NumberInputField";
import { Row, Col, Form, Accordion } from "react-bootstrap";
import { FaExclamationCircle, FaInfoCircle } from "react-icons/fa";
import { FormattedMessage, useIntl } from "react-intl";
import { EnumSelectField } from "../../bootstrap/EnumSelectField";
import ToolTip from "../../bootstrap/ToolTip";
import { Arrays } from "../../../utils/Arrays";
import { EaHRMapping } from "./EaHRMapping";
import { EaHRMappingTotals } from "./EaHRMappingTotals";

export function EaAccreditationFormStep4({
  formStep,
  numberOfSteps,
  errors,
  formikValues,
  handleChange,
  handlePreviousStep,
  handleNextStep,
  setFieldValue,
  qualificationLevelOptions,
  managerQualificationLevelOptions,
  districts,
  renewalStartDate,
  renewalEndDate,
  readMode = false,
}) {
  const intl = useIntl();
  const internalOrExternalOptions = ["INTERN", "EXTERN"];

  const districtOptions = districts.filter((d) =>
    formikValues.interventionDistrictCodes.includes(d.code)
  );
  const otherDistrictOptions = districts.filter(
    (d) => !formikValues.interventionDistrictCodes.includes(d.code)
  );

  const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("pt-PT");
  };

  const getInactivePeriod = (deactivatedAt) => {
    if (!deactivatedAt) return "";
    return `[Desativado a ${formatDate(deactivatedAt)}]`;
  };

  function addTGP() {
    const newTGP = {
      isNew: true,
      active: true,
      name: "",
      qualificationLevelCode: "-1",
      hre: {
        nif: "",
        bondType: "-1",
        projectCreationCount: 0,
        approvedProjectFollowUpCount: 0,
        consultingCount: 0,
        expandSkillsCount: 0,
        analysisCount: 0,
      },
      hrMapList: [],
    };

    setFieldValue("tgpList", [...formikValues.tgpList, newTGP]);
  }

  function removeTGP(index) {
    const next = [...formikValues.tgpList];
    next.splice(index, 1);
    setFieldValue("tgpList", next);
  }

  function cancelNewTGP(index) {
    const item = formikValues.tgpList[index];
    if (item && item.isNew) removeTGP(index);
  }

  function toggleDeactivate(index) {
    const next = [...formikValues.tgpList];
    const cur = next[index];
    next[index] = {
      ...cur,
      active: cur.active === false ? true : false,
    };
    setFieldValue("tgpList", next);
  }

  function checkBoxOnChangeHandler(index, property) {
    const cur = !!formikValues.getList[index][property];
    setFieldValue(`getList[${index}].${property}`, !cur);
  }

  function totalTgpListExperienceByArea(area) {
    let total = 0;
    formikValues.tgpList.forEach((tgp) => {
      if (tgp.active === false) {
        return;
      }
      const hre = tgp.hre || {};
      total += hre[area] || 0;
    });
    return total;
  }

  const toDate = (v) => (v ? new Date(v) : null);

  const isRelevantForCurrentRenewal = (hr) => {
    if (!renewalStartDate) return true;
    if (!hr.deactivatedAt) return true;

    const start = toDate(renewalStartDate);
    const end = toDate(hr.deactivatedAt);
    if (!start || !end) return true;
    return end >= start;
  };

  /* const tgpHasMappings = (tgp) =>
    Arrays.isNotEmpty(tgp.hrMapList) && tgp.hrMapList.length > 4; */

  const getsByRD = React.useMemo(() => {
    const m = {};
    (formikValues.getList || []).forEach((g, index) => {
      const rd = g.regionalDelegationCode || "";
      if (!rd) return;
      if (!m[rd]) m[rd] = { existing: [], news: [] };
      const entry = { item: g, index };
      if (g.isNew) m[rd].news.push(entry);
      else m[rd].existing.push(entry);
    });
    return m;
  }, [formikValues.getList]);

  return (
    <div>
      <h4 className="text-primary mb-0">
        <FormattedMessage id="eaAccreditationForm.step4.title" />
      </h4>
      <small className="text-secondary text-uppercase">
        {intl.formatMessage(
          { id: "all.step" },
          { step: formStep, numberOfSteps: numberOfSteps }
        )}
      </small>

      <Row>
        <Col>
          <Form.Label>
            <FormattedMessage id="eaAccreditationForm.step4.eaHumanResources" />
          </Form.Label>
        </Col>
      </Row>

      {Object.keys(getsByRD).map((rd) => {
        const bucket = getsByRD[rd];
        const existing = bucket.existing || [];
        const news = bucket.news || [];

        if (readMode) {
          const allExisting = existing;
          const withPos = allExisting.map((e, pos) => ({ ...e, _pos: pos }));

          let display = [];

          if (withPos.length > 0) {
            const currentCandidates = withPos.filter(
              (e) => e.item.active !== false
            );
            const currentEntry =
              currentCandidates[currentCandidates.length - 1] ||
              withPos[withPos.length - 1];

            const remaining = withPos.filter(
              (e) => e._pos !== currentEntry._pos
            );
            const previousEntry =
              remaining.length > 0 ? remaining[remaining.length - 1] : null;

            if (previousEntry) {
              display.push({ ...previousEntry, kind: "old" });
            }
            if (currentEntry) {
              display.push({ ...currentEntry, kind: "current" });
            }
          }

          return display.map(({ item: get, index, kind }, idx) => {
            const isOld = kind === "old";
            const headerId = isOld
              ? "eaAccreditationForm.step4.accordion.function.get.old"
              : "eaAccreditationForm.step4.accordion.function.get";

            return (
              <Row key={`${rd}-${index}-${idx}`}>
                <Col>
                  <Accordion>
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        <span className="d-flex align-items-center">
                          {errors.getList?.[index]?.qualificationLevelCode && (
                            <FaExclamationCircle />
                          )}
                          <FormattedMessage id={headerId} />
                          {" - " + get.regionalDelegationCode}
                          {isOld && get.deactivatedAt && (
                            <span className="ms-2">
                              {getInactivePeriod(get.deactivatedAt)}
                            </span>
                          )}
                        </span>
                      </Accordion.Header>
                      <Accordion.Body>
                        {get.name ? (
                          <>
                            <Row>
                              <Col>
                                <Form.Label className="mt-0">
                                  <FormattedMessage id="eaAccreditationForm.step4.accordion.header.name" />
                                </Form.Label>
                                <div>{get.name || "-"}</div>
                              </Col>
                            </Row>

                            <Row>
                              <Col md="6">
                                <EnumSelectField
                                  options={managerQualificationLevelOptions}
                                  nullOption={true}
                                  preffixDescriptionId="eaAccreditationForm.qualificationLevelCode.option"
                                  value={get.qualificationLevelCode}
                                  name={`getList[${index}].qualificationLevelCode`}
                                  labelId={
                                    "eaAccreditationForm.step4.accordion.header.academicQualification"
                                  }
                                  handleChange={handleChange}
                                  isInvalid={
                                    errors.getList?.[index]
                                      ?.qualificationLevelCode
                                  }
                                  errorMessage={
                                    errors.getList?.[index]
                                      ?.qualificationLevelCode
                                  }
                                  disabled={true}
                                />
                              </Col>
                            </Row>
                            <Row className="mt-2">
                              <Col>
                                <Form.Check
                                  label={intl.formatMessage({
                                    id: "eaAccreditationForm.step4.accordion.header.managementExperience",
                                  })}
                                  checked={get.managementExperience === true}
                                  onChange={() =>
                                    checkBoxOnChangeHandler(
                                      index,
                                      "managementExperience"
                                    )
                                  }
                                  disabled={true}
                                />
                              </Col>
                            </Row>
                          </>
                        ) : (
                          <>
                            <div className="text-secondary">
                              <FaInfoCircle size="30" />
                            </div>
                            <p className="mt-3 text-secondary">
                              Tem de preencher no passo 1 (Identificação da EA)
                              a informação do gestor técnico
                            </p>
                          </>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </Col>
              </Row>
            );
          });
        }

        const replacementNew =
          news.find((n) => n.item.anchorIndex != null) || null;

        const standaloneNew =
          news.find((n) => n.item.anchorIndex == null) || null;

        let currentExisting = null;
        if (existing.length > 0) {
          const withPosExisting = existing.map((e, pos) => ({
            ...e,
            _pos: pos,
          }));
          const currentCandidates = withPosExisting.filter(
            (e) => e.item.active !== false
          );
          currentExisting =
            currentCandidates[currentCandidates.length - 1] ||
            withPosExisting[withPosExisting.length - 1];
        }

        const current =
          replacementNew || standaloneNew || currentExisting || null;

        if (!current) return null;

        const { item: get, index } = current;
        const headerId = "eaAccreditationForm.step4.accordion.function.get";

        return (
          <Row key={`rd-${rd}`}>
            <Col>
              <Accordion>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <span className="d-flex align-items-center">
                      {errors.getList?.[index]?.qualificationLevelCode && (
                        <FaExclamationCircle />
                      )}
                      <FormattedMessage id={headerId} />
                      {" - " + rd}
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    {get.name ? (
                      <>
                        <Row>
                          <Col>
                            <Form.Label className="mt-0">
                              <FormattedMessage id="eaAccreditationForm.step4.accordion.header.name" />
                            </Form.Label>
                            <div>{get.name || "-"}</div>
                          </Col>
                        </Row>

                        <Row>
                          <Col md="6">
                            <EnumSelectField
                              options={managerQualificationLevelOptions}
                              nullOption={true}
                              preffixDescriptionId="eaAccreditationForm.qualificationLevelCode.option"
                              value={get.qualificationLevelCode}
                              name={`getList[${index}].qualificationLevelCode`}
                              labelId={
                                "eaAccreditationForm.step4.accordion.header.academicQualification"
                              }
                              handleChange={handleChange}
                              isInvalid={
                                errors.getList?.[index]?.qualificationLevelCode
                              }
                              errorMessage={
                                errors.getList?.[index]?.qualificationLevelCode
                              }
                              disabled={true}
                            />
                          </Col>
                        </Row>
                        <Row className="mt-2">
                          <Col>
                            <Form.Check
                              label={intl.formatMessage({
                                id: "eaAccreditationForm.step4.accordion.header.managementExperience",
                              })}
                              checked={get.managementExperience === true}
                              onChange={() =>
                                checkBoxOnChangeHandler(
                                  index,
                                  "managementExperience"
                                )
                              }
                              disabled={true}
                            />
                          </Col>
                        </Row>
                      </>
                    ) : (
                      <>
                        <div className="text-secondary">
                          <FaInfoCircle size="30" />
                        </div>
                        <p className="mt-3 text-secondary">
                          Tem de preencher no passo 1 (Identificação da EA) a
                          informação do gestor técnico
                        </p>
                      </>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Col>
          </Row>
        );
      })}

      {formikValues.tgpList.map((tgp, index) => {
        const isNew = !!tgp.isNew;
        const isInactive = tgp.active === false;
        const isOld = isInactive && !!tgp.deactivatedAt && readMode;

        /* if (!readMode) {
          if (!isNew && isInactive && tgp.deactivatedAt) {
            return null;
          }
        } */

        const headerId = isNew
          ? "eaAccreditationForm.step4.accordion.function.tgp.new"
          : isOld
          ? "eaAccreditationForm.step4.accordion.function.tgp.old"
          : "eaAccreditationForm.step4.accordion.function.tgp";

        return (
          <Row key={index}>
            <Col>
              <Accordion defaultActiveKey={isNew ? "0" : undefined}>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <span className="d-flex align-items-center">
                      {errors.tgpList?.[index] && <FaExclamationCircle />}
                      <FormattedMessage id={headerId} />

                      {/* {!isNew && !readMode && isInactive && (
                        <span className="badge bg-secondary ms-2">
                          <FormattedMessage id="eaAccreditationForm.step4.badge.inactive" />
                        </span>
                      )} */}

                      {/* isOld && */ tgp.deactivatedAt && (
                        <span className="ms-2">
                          {getInactivePeriod(tgp.deactivatedAt)}
                        </span>
                      )}
                    </span>
                  </Accordion.Header>

                  <Accordion.Body>
                    <Row>
                      <Col>
                        {isNew ? (
                          <div style={{ marginTop: "-1rem" }}>
                            <TextInputField
                              name={`tgpList[${index}].name`}
                              labelId="eaAccreditationForm.step4.accordion.header.name"
                              handleChange={handleChange}
                              isInvalid={errors.tgpList?.[index]?.name}
                              errorMessage={errors.tgpList?.[index]?.name}
                              value={tgp.name}
                              disabled={false}
                            />
                          </div>
                        ) : (
                          <>
                            <Form.Label className="mt-0">
                              <FormattedMessage id="eaAccreditationForm.step4.accordion.header.name" />
                            </Form.Label>
                            <div>{tgp.name || "-"}</div>
                          </>
                        )}
                      </Col>
                    </Row>

                    {/* !isInactive && */ (
                      <>
                        <Row className="mt-2">
                          <Col md="6">
                            <Form.Label>
                              <FormattedMessage id="eaAccreditationForm.step4.accordion.header.academicQualification" />
                            </Form.Label>
                            <EnumSelectField
                              options={qualificationLevelOptions}
                              nullOption={true}
                              preffixDescriptionId="eaAccreditationForm.qualificationLevelCode.option"
                              value={tgp.qualificationLevelCode}
                              name={`tgpList[${index}].qualificationLevelCode`}
                              handleChange={handleChange}
                              isInvalid={
                                errors.tgpList?.[index]?.qualificationLevelCode
                              }
                              errorMessage={
                                errors.tgpList?.[index]?.qualificationLevelCode
                              }
                              disabled={!isNew}
                            />
                          </Col>
                          <Col md="3">
                            <Form.Label>
                              <FormattedMessage id="eaAccreditationForm.step4.accordion.header.nif" />
                            </Form.Label>
                            <NumberInputField
                              value={tgp.hre.nif}
                              name={`tgpList[${index}].hre.nif`}
                              handleChange={handleChange}
                              isInvalid={errors.tgpList?.[index]?.hre?.nif}
                              errorMessage={errors.tgpList?.[index]?.hre?.nif}
                              maxLength={9}
                              disabled={!isNew}
                            />
                          </Col>
                          <Col md="3">
                            <Form.Label>
                              <FormattedMessage id="eaAccreditationForm.step4.accordion.header.bondType" />
                            </Form.Label>
                            <EnumSelectField
                              options={internalOrExternalOptions}
                              nullOption={true}
                              preffixDescriptionId="eaAccreditationForm.bondType.option"
                              value={tgp.hre.bondType}
                              name={`tgpList[${index}].hre.bondType`}
                              handleChange={handleChange}
                              isInvalid={errors.tgpList?.[index]?.hre?.bondType}
                              errorMessage={
                                errors.tgpList?.[index]?.hre?.bondType
                              }
                              disabled={!isNew}
                            />
                          </Col>
                        </Row>
                      </>
                    )}

                    <Row className="mt-4">
                      <Col>
                        <small className="text-uppercase text-primary">
                          <FormattedMessage id="eaAccreditationForm.step4.accordion.header.experience" />
                          <ToolTip
                            message={
                              <FormattedMessage id="eaAccreditationForm.step4.accordion.header.experience.tooltip" />
                            }
                          />
                        </small>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="4">
                        <Form.Label>
                          <FormattedMessage id="eaAccreditationForm.step4.accordion.header.projectCreation" />
                        </Form.Label>
                        {isNew ? (
                          <NumberInputField
                            maxLength={2}
                            className="w-25"
                            value={tgp.hre.projectCreationCount}
                            name={`tgpList[${index}].hre.projectCreationCount`}
                            handleChange={handleChange}
                            isInvalid={
                              errors.tgpList?.[index]?.hre?.projectCreationCount
                            }
                            errorMessage={
                              errors.tgpList?.[index]?.hre?.projectCreationCount
                            }
                            disabled={false}
                          />
                        ) : (
                          <div>{tgp.hre.projectCreationCount ?? 0}</div>
                        )}
                      </Col>
                      <Col md="4">
                        <Form.Label>
                          <FormattedMessage id="eaAccreditationForm.step4.accordion.header.candidatureReview" />
                        </Form.Label>
                        {isNew ? (
                          <NumberInputField
                            maxLength={2}
                            className="w-25"
                            value={tgp.hre.analysisCount}
                            name={`tgpList[${index}].hre.analysisCount`}
                            handleChange={handleChange}
                            isInvalid={
                              errors.tgpList?.[index]?.hre?.analysisCount
                            }
                            errorMessage={
                              errors.tgpList?.[index]?.hre?.analysisCount
                            }
                            disabled={false}
                          />
                        ) : (
                          <div>{tgp.hre.analysisCount ?? 0}</div>
                        )}
                      </Col>
                      <Col md="4">
                        <Form.Label>
                          <FormattedMessage id="eaAccreditationForm.step4.accordion.header.consultancy" />
                        </Form.Label>
                        {isNew ? (
                          <NumberInputField
                            maxLength={2}
                            className="w-25"
                            value={tgp.hre.consultingCount}
                            name={`tgpList[${index}].hre.consultingCount`}
                            handleChange={handleChange}
                            isInvalid={
                              errors.tgpList?.[index]?.hre?.consultingCount
                            }
                            errorMessage={
                              errors.tgpList?.[index]?.hre?.consultingCount
                            }
                            disabled={false}
                          />
                        ) : (
                          <div>{tgp.hre.consultingCount ?? 0}</div>
                        )}
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <Form.Label>
                          <FormattedMessage id="eaAccreditationForm.step4.accordion.header.skillsExtension" />
                        </Form.Label>
                        {isNew ? (
                          <NumberInputField
                            maxLength={2}
                            className="w-25"
                            value={tgp.hre.expandSkillsCount}
                            name={`tgpList[${index}].hre.expandSkillsCount`}
                            handleChange={handleChange}
                            isInvalid={
                              errors.tgpList?.[index]?.hre?.expandSkillsCount
                            }
                            errorMessage={
                              errors.tgpList?.[index]?.hre?.expandSkillsCount
                            }
                            disabled={false}
                          />
                        ) : (
                          <div>{tgp.hre.expandSkillsCount ?? 0}</div>
                        )}
                      </Col>
                      <Col md="6">
                        <Form.Label>
                          <FormattedMessage id="eaAccreditationForm.step4.accordion.header.followUp" />
                        </Form.Label>
                        {isNew ? (
                          <NumberInputField
                            maxLength={2}
                            className="w-25"
                            value={tgp.hre.approvedProjectFollowUpCount}
                            name={`tgpList[${index}].hre.approvedProjectFollowUpCount`}
                            handleChange={handleChange}
                            isInvalid={
                              errors.tgpList?.[index]?.hre
                                ?.approvedProjectFollowUpCount
                            }
                            errorMessage={
                              errors.tgpList?.[index]?.hre
                                ?.approvedProjectFollowUpCount
                            }
                            disabled={false}
                          />
                        ) : (
                          <div>{tgp.hre.approvedProjectFollowUpCount ?? 0}</div>
                        )}
                      </Col>
                    </Row>

                    {/*!isInactive && !tgpHasMappings(tgp) && */ (
                      <>
                        <Row className="mt-5">
                          <Col>
                            <small className="text-uppercase text-primary">
                              <FormattedMessage id="eaAccreditationForm.step4.accordion.header.districtAssignment" />
                            </small>
                          </Col>
                        </Row>
                        <EaHRMapping
                          tgp={tgp}
                          index={index}
                          errors={errors}
                          districtOptions={districtOptions}
                          otherDistrictOptions={otherDistrictOptions}
                          districts={districts}
                          setFieldValue={setFieldValue}
                          isNew={isNew}
                        />
                      </>
                    )}

                    {isNew && !readMode && (
                      <Row className="mt-4">
                        <Col>
                          <button
                            className="btn btn-link px-0"
                            type="button"
                            onClick={() => cancelNewTGP(index)}
                          >
                            <FormattedMessage id="all.cancel" />
                          </button>
                        </Col>
                      </Row>
                    )}

                    {!isNew && !readMode && (
                      <Row className="mt-4">
                        <Col>
                          <button
                            className="btn btn-link px-0"
                            type="button"
                            onClick={() => toggleDeactivate(index)}
                          >
                            {isInactive ? (
                              <FormattedMessage id="eaAccreditationForm.step4.action.cancelDeactivation" />
                            ) : (
                              <FormattedMessage id="eaAccreditationForm.step4.action.deactivate" />
                            )}
                          </button>
                        </Col>
                      </Row>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Col>
          </Row>
        );
      })}

      {!readMode && (
        <Row className="mt-4">
          <Col>
            <button
              className="btn btn-outline-primary"
              type="button"
              onClick={addTGP}
            >
              <FormattedMessage id="eaAccreditationForm.step4.button.addTGP" />
            </button>
          </Col>
        </Row>
      )}

      {formikValues.tgpList.length > 0 && (
        <>
          <Row className="mt-5">
            <Col>
              <FormattedMessage id="eaAccreditationForm.step4.accordion.tgpTotalExperience" />{" "}
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <Form.Label>
                <FormattedMessage id="eaAccreditationForm.step4.accordion.header.projectCreation" />
              </Form.Label>
              {totalTgpListExperienceByArea("projectCreationCount")}
            </Col>
            <Col md="4">
              <Form.Label>
                <FormattedMessage id="eaAccreditationForm.step4.accordion.header.candidatureReview" />
              </Form.Label>
              {totalTgpListExperienceByArea("analysisCount")}
            </Col>
            <Col md="4">
              <Form.Label>
                <FormattedMessage id="eaAccreditationForm.step4.accordion.header.consultancy" />
              </Form.Label>
              {totalTgpListExperienceByArea("consultingCount")}
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <Form.Label>
                <FormattedMessage id="eaAccreditationForm.step4.accordion.header.skillsExtension" />
              </Form.Label>
              {totalTgpListExperienceByArea("expandSkillsCount")}
            </Col>
            <Col md="6">
              <Form.Label>
                <FormattedMessage id="eaAccreditationForm.step4.accordion.header.followUp" />
              </Form.Label>
              {totalTgpListExperienceByArea("approvedProjectFollowUpCount")}
            </Col>
          </Row>
        </>
      )}

      <Row className="mt-5">
        <Col>
          <FormattedMessage id="eaAccreditationForm.step4.accordion.tgpTotalSkills" />{" "}
        </Col>
      </Row>
      <Row>
        <Col>
          <EaHRMappingTotals
            tgps={formikValues.tgpList}
            districtOptions={districtOptions}
          />
        </Col>
      </Row>

      <Row className="mt-5">
        <Col>
          <p>
            <FormattedMessage id="eaAccreditationForm.step4.accordion.total" />{" "}
            {" " + (formikValues.getList.length + formikValues.tgpList.length)}
          </p>
        </Col>
      </Row>

      <div className="form-actions mt-5">
        <button
          className="btn btn-link"
          type="button"
          onClick={handlePreviousStep}
        >
          <FormattedMessage id="all.back" />
        </button>
        <button
          className="btn btn-primary"
          type="button"
          onClick={handleNextStep}
        >
          <FormattedMessage id="all.nextStep" />
        </button>
      </div>
    </div>
  );
}
